import { EditOutlined, UserAddOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Tag, Tooltip } from "antd";
import { useEffect, useState } from "react";
import * as Styles from "./ManageContactSafetyDocStyles";
import { API_URL } from "../../../API/Config";
import ReTable from "../../../unity-ui-common/Components/ReTable/ReTable";
import { convertDateInReadableFormat } from "../../../Helper/Methods";
import { role } from "../../../API/UserManagement/Role";
import { Uniview } from "../../../API/Uniview";

// Separator used to build a composite row id "<masterDistributionId>::<Role>".
// The backend (updateSafetyDocument) splits on this to create one distribution
// record per (contact, role).
const ROLE_ID_SEPARATOR = "::";

// Expand each fetched contact into one row per role so that a contact holding
// multiple roles can be selected (and added) role-by-role.
function splitContactsByRole(contacts: any[]): any[] {
  const rows: any[] = [];
  (contacts || []).forEach((contact: any) => {
    const roles = String(contact?.rol_mat ?? "")
      .split(",")
      .map((r: string) => r.trim())
      .filter((r: string) => r.length > 0);

    if (roles.length === 0) {
      // No roles resolved – keep a single row (added as-is, legacy behaviour).
      rows.push({ ...contact, id: String(contact.id), rol_mat: "-" });
      return;
    }

    roles.forEach((roleName: string) => {
      rows.push({
        ...contact,
        id: `${contact.id}${ROLE_ID_SEPARATOR}${roleName}`,
        rol_mat: roleName,
      });
    });
  });
  return rows;
}

interface ManageContactSafetyDocProps {
  onSelectContacts: (ids: string[]) => void;
  safetyDocumentID: string;
  distributions: any; // Define the type of distributions if possible
  setFlag: (flag: boolean) => void;
  flag: boolean;
  setVisibility: (visible: boolean) => void;
  ReNotification: (options: { description: string; duration: number; placement: string; type: string }) => void;
}

function ManageContactSafetyDoc({
  onSelectContacts,
  safetyDocumentID,
  distributions,
  setFlag,
  flag,
  setVisibility,
  ReNotification,
}: ManageContactSafetyDocProps) {
  const [selectedTableRow, setSelectedTableRow] = useState<string[]>([]);
  const [refreshTable, setRefreshTable] = useState(false);
  const [commonRefresh, setCommonRefresh] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);

  // Fetch all contacts once and expand them into one row per role. We fetch the
  // full list (instead of using ReTable's server-side uniView pagination) so the
  // role-split rows paginate correctly on the client – server pagination counts
  // contacts, not role rows, which would otherwise truncate the expanded list.
  useEffect(() => {
    let isMounted = true;
    const fetchContacts = async () => {
      setLoading(true);
      try {
        const dataFetcher = new Uniview("user", "allUsers");
        const res = await dataFetcher.fetchData(
          {},
          { orderBy: { updated_at: "DESC" }, limit: 10000 }
        );
        if (isMounted && res?.status) {
          setTableData(splitContactsByRole(res.data || []));
        }
      } catch (error) {
        console.error("Failed to load contacts for Add Contact modal", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchContacts();
    return () => {
      isMounted = false;
    };
  }, []);

  //  const handleSubmit = async () => {
  //   if (!safetyDocumentID) {
  //     ReNotification({
  //       description: "Safety Document ID not found.",
  //       duration: 1000,
  //       placement: "bottomLeft",
  //       type: "error",
  //     });
  //     return;
  //   }
  
  //   if (selectedTableRow.length === 0) {
  //     ReNotification({
  //       description: "Please select at least one contact.",
  //       duration: 1000,
  //       placement: "bottomLeft",
  //       type: "error",
  //     });
  //     return;
  //   }

  //     setLoading(true);
  
  //     // Simulate sending the selected contacts (using `distributions.addContacts` or similar)
  //     try {
  //       const res = await distributions.addContacts(safetyDocumentID, selectedTableRow);
  
  //       if (!res?.statusCode) {
  //         ReNotification({
  //           description: "Contacts added successfully.",
  //           duration: 1000,
  //           placement: "bottomLeft",
  //           type: "success",
  //         });
  //         setFlag(!flag); // Trigger flag to refresh data or UI
  //         setVisibility(false); // Close the modal
  //       }  else {
  //         ReNotification({
  //           description: `Error: ${res.error || "Unknown error occurred."}`,
  //           duration: 1000,
  //           placement: "bottomLeft",
  //           type: "error",
  //         });
  //       }
  //     } catch (error) {
  //       ReNotification({
  //         description: "An error occurred while adding contacts.",
  //         duration: 1000,
  //         placement: "bottomLeft",
  //         type: "error",
  //       });
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  const columns = [
    // {
    //   title: "Contact Id",
    //   dataIndex: "id",
    //   key: "id",
    //   width: 200,
    // },
    {
      title: "Contact Name",
      dataIndex: "name",
      key: "name",
      width: 250,
    },
    {
      title: "Role",
      dataIndex: "rol_mat",
      key: "rol_mat",
      width: 250,
    },
    {
      title: "Contact Country",
      dataIndex: "country",
      key: "country",
      width: 250,
    },
    {
      title: "Mode of Correspondence",
      dataIndex: "mode_of_correspondence",
      key: "mode_of_correspondence",
      width: 250
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value: any) => {
        const isActive =
          value === 1 ||
          value === "1" ||
          value === true ||
          value === "true" ||
          (typeof value === "string" && value.toLowerCase() === "active");
        const text = isActive ? "Active" : "Inactive";
        const color = isActive ? "green" : "red";
        return (
          <Tag color={color} title="status">
            {text}
          </Tag> 
        );
      },
      width: 250,
    },
    {
      title: "Site Number",
      dataIndex: "site_number",
      key: "site_number",
      width: 250,
    },
    {
      title: "Study Number",
      dataIndex: "study_number",
      key: "study_number",
      width: 250,
    },
    {
      title: "Last Modified Date",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 250,
      render: (value: any, record: any) => {
        const text = record.updated_at
          ? `${convertDateInReadableFormat(record.updated_at)}`
          : "-";
        return text;
      },
    },
  ];
  useEffect(() => {
    setRefreshTable(commonRefresh);
  }, [commonRefresh]);

  useEffect(() => {
    // Pass selected contact IDs to the parent modal
    onSelectContacts(selectedTableRow);
  }, [selectedTableRow, onSelectContacts]);
  
  return (
    <Styles.Container>
      <ReTable
        name="All Users"
        // title="All Users"
        columns={columns}
        data={tableData}
        loading={loading}
        columnOptions={{
          sorting: {
            columns: [
              "id",
              "name",
              "rol_mat",
              "country",
              "status",
              "site_number",
              "study_number",
              "updated_at",
              "mode_of_correspondence",
            ],
            sortingDirections: ["ascend", "descend"],
          },
          filter: {
            filterSearch: true,
            columns: [
              "id",
              "name",

              "rol_mat",

              "country",
              {
                key: "status",
                items: [
                  {
                    text: "Active",
                    value: "1",
                  },
                  {
                    text: "Inactive",
                    value: "0",
                  },
                ],
              },
              "site_number",

              "study_number",
              "mode_of_correspondence",
              {
                key: "updated_at",
                action: (date: any) =>
                  date ? convertDateInReadableFormat(date) : "-",
              },
            ],
          },
        }}
        refresh={refreshTable}
        scroll={{
          x: 800,
          y: 500,
        }}
        rowSelection={{
          selectedRowKeys: selectedTableRow,
          type: "checkbox",
          setSelectedRowKeys: setSelectedTableRow,
          rowKey: "id",

        }}
      />
    </Styles.Container>
  );
}

export default ManageContactSafetyDoc;
